import React, { useState } from 'react';
import { 
    Layers, Globe, Activity, CloudLightning, DollarSign, Check, Menu, X, 
    ChevronRight, Mail, Phone, MapPin, ArrowRight, Lock, Shield, Star, 
    FileText, Warehouse, CheckCircle2, Send
} from 'lucide-react';

const LandingPage = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isAnnual, setIsAnnual] = useState(true);
    const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const toggleBilling = () => setIsAnnual(!isAnnual);

    const handleContactSubmit = (e) => {
        e.preventDefault();
        if (!contactForm.name || !contactForm.email || !contactForm.message) return;
        setSubmitting(true);
        // Simulate API call
        setTimeout(() => {
            setSubmitting(false);
            setFormSubmitted(true);
            setContactForm({ name: '', email: '', message: '' });
            setTimeout(() => setFormSubmitted(false), 5000);
        }, 800);
    };

    const features = [
        {
            icon: <Globe className="w-6 h-6 text-indigo-400" />,
            title: "Multi-Tenant Architecture",
            desc: "Isolate supplier catalogs, customer records, and orders with secure realm integrations."
        },
        {
            icon: <Warehouse className="w-6 h-6 text-emerald-400" />,
            title: "Warehouse & Picking Logs",
            desc: "Real-time picker revision states and automated packing lists to optimize stock fulfillment."
        },
        {
            icon: <CloudLightning className="w-6 h-6 text-cyan-400" />,
            title: "Direct S3 Storage Uploader",
            desc: "Client-side 1:1 image cropping and WebP compression directly to OCI Object Storage."
        },
        {
            icon: <FileText className="w-6 h-6 text-amber-400" />,
            title: "Daily Operations Reports",
            desc: "Automated Daily Sales PDF creation to keep track of storekeeper performance and metrics."
        },
        {
            icon: <Shield className="w-6 h-6 text-rose-400" />,
            title: "Keycloak Role Security",
            desc: "Granular access controls mapping Tenant Owner, Salesperson, and Warehouse roles."
        },
        {
            icon: <Activity className="w-6 h-6 text-purple-400" />,
            title: "Fitted Mobile UI",
            desc: "Tailored customer catalogs, bottom navigation bars, and offline-ready search indices."
        }
    ];

    const pricingTiers = [
        {
            name: "Basic",
            price: isAnnual ? 199 : 249,
            desc: "Perfect for single suppliers getting started with digital ordering.",
            features: [
                "Up to 250 products",
                "Basic Multi-tenancy support",
                "Manual sales order flow",
                "Email support",
                "5GB Image Storage"
            ],
            cta: "Get Started",
            popular: false
        },
        {
            name: "Standard",
            price: isAnnual ? 399 : 499,
            desc: "Designed for growing wholesalers with dedicated picking teams.",
            features: [
                "Unlimited products",
                "Warehouse & Picking flow",
                "Client-side image optimizer",
                "Salesperson re-assignments",
                "Priority 24/7 Support",
                "50GB Image Storage"
            ],
            cta: "Start Free Trial",
            popular: true
        },
        {
            name: "Enterprise",
            price: "Custom",
            desc: "For multi-warehouse operations requiring bespoke integrations.",
            features: [
                "Everything in Standard",
                "Bespoke database schemas",
                "Dedicated Keycloak instance",
                "SLA uptime guarantee",
                "Custom report templates",
                "Unlimited Storage"
            ],
            cta: "Contact Sales",
            popular: false
        }
    ];

    const stats = [
        { val: "10M+", label: "Orders Placed" },
        { val: "99.99%", label: "Uptime SLA" },
        { val: "5K+", label: "Suppliers Joined" },
        { val: "0ms", label: "Image Latency" }
    ];

    return (
        <div className="h-[100dvh] w-full overflow-y-auto scroll-smooth bg-slate-950 text-slate-100 flex flex-col font-sans relative">
            
            {/* Background Gradients */}
            <div className="absolute top-0 left-0 w-full h-[600px] bg-gradient-to-b from-indigo-500/10 via-purple-500/5 to-transparent pointer-events-none z-0" />
            <div className="absolute top-[20%] right-[10%] w-[300px] h-[300px] bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none z-0" />
            <div className="absolute top-[50%] left-[5%] w-[400px] h-[400px] bg-indigo-500/5 rounded-full blur-[160px] pointer-events-none z-0" />

            {/* Navigation Header */}
            <header className="sticky top-0 w-full bg-slate-950/80 backdrop-blur-md border-b border-slate-900 z-50 transition-all">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    {/* Brand */}
                    <div className="flex items-center gap-2 cursor-pointer">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                            <Layers className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-xl font-black bg-gradient-to-r from-white to-slate-200 bg-clip-text text-transparent tracking-tight">yaseero</span>
                    </div>

                    {/* Desktop nav */}
                    <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-slate-400">
                        <a href="#features" className="hover:text-white transition-colors">Features</a>
                        <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
                        <a href="#about" className="hover:text-white transition-colors">About</a>
                        <a href="#contact" className="hover:text-white transition-colors">Contact</a>
                    </nav>

                    {/* CTAs */}
                    <div className="hidden md:flex items-center gap-4">
                        <a 
                            href="/login" 
                            className="text-sm font-semibold text-slate-300 hover:text-white transition-colors px-4 py-2"
                        >
                            Sign In
                        </a>
                        <a 
                            href="/register" 
                            className="bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2 rounded-xl text-sm font-bold shadow-md shadow-indigo-600/20 hover:shadow-indigo-600/30 transition-all"
                        >
                            Get Started
                        </a>
                    </div>

                    {/* Mobile menu button */}
                    <button 
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="md:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-900 transition-all"
                    >
                        {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>

                {/* Mobile Menu */}
                {isMenuOpen && (
                    <div className="md:hidden bg-slate-950 border-b border-slate-900 px-4 py-6 space-y-4 flex flex-col font-semibold animate-fade-in">
                        <a 
                            href="#features" 
                            onClick={() => setIsMenuOpen(false)}
                            className="text-slate-400 hover:text-white py-2 block"
                        >
                            Features
                        </a>
                        <a 
                            href="#pricing" 
                            onClick={() => setIsMenuOpen(false)}
                            className="text-slate-400 hover:text-white py-2 block"
                        >
                            Pricing
                        </a>
                        <a 
                            href="#about" 
                            onClick={() => setIsMenuOpen(false)}
                            className="text-slate-400 hover:text-white py-2 block"
                        >
                            About
                        </a>
                        <a 
                            href="#contact" 
                            onClick={() => setIsMenuOpen(false)}
                            className="text-slate-400 hover:text-white py-2 block"
                        >
                            Contact
                        </a>
                        <div className="w-full h-px bg-slate-900 my-2" />
                        <div className="flex gap-4 items-center">
                            <a 
                                href="/login" 
                                className="text-center flex-1 py-2.5 rounded-xl border border-slate-800 text-slate-300 hover:text-white transition-all text-sm"
                            >
                                Sign In
                            </a>
                            <a 
                                href="/register" 
                                className="text-center flex-1 py-2.5 rounded-xl bg-indigo-600 text-white font-bold text-sm shadow-md shadow-indigo-600/20"
                            >
                                Get Started
                            </a>
                        </div>
                    </div>
                )}
            </header>

            {/* Hero Section */}
            <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 flex flex-col items-center text-center z-10">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 text-xs font-bold mb-6 hover:bg-indigo-500/20 transition-all select-none">
                    <Star className="w-3.5 h-3.5 fill-indigo-400 text-indigo-400" />
                    <span>Introducing Real-Time Warehouse Picking Logs</span>
                </div>
                
                <h1 className="text-4xl sm:text-6xl font-black tracking-tight max-w-4xl leading-[1.1] text-transparent bg-gradient-to-r from-white via-slate-100 to-slate-400 bg-clip-text">
                    Streamline Wholesale Orders & B2B Sales Orchestration
                </h1>
                
                <p className="mt-6 text-base sm:text-xl text-slate-400 max-w-2xl font-medium leading-relaxed">
                    Yaseero bridges suppliers, salespersons, and retail customers. Sync inventories, compile daily sales reports, and track picking workflows seamlessly in a unified multi-tenant app.
                </p>

                <div className="mt-10 flex flex-col sm:flex-row items-center gap-4">
                    <a 
                        href="/register" 
                        className="w-full sm:w-auto bg-gradient-to-r from-indigo-500 to-cyan-500 hover:from-indigo-600 hover:to-cyan-600 text-white px-8 py-3.5 rounded-xl text-base font-bold shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/30 hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
                    >
                        <span>Start Free Trial</span>
                        <ArrowRight className="w-5 h-5" />
                    </a>
                    <a 
                        href="#features" 
                        className="w-full sm:w-auto border border-slate-800 bg-slate-950/40 hover:bg-slate-900/60 text-slate-300 hover:text-white px-8 py-3.5 rounded-xl text-base font-bold transition-all flex items-center justify-center"
                    >
                        Explore Features
                    </a>
                </div>

                {/* Dashboard Mockup Layout */}
                <div className="mt-16 w-full max-w-5xl rounded-2xl border border-slate-800 bg-slate-950 p-2 shadow-2xl shadow-indigo-500/5 relative overflow-hidden group select-none">
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent pointer-events-none z-10" />
                    <div className="h-6 w-full bg-slate-900 flex items-center gap-1.5 px-4 border-b border-slate-800">
                        <div className="w-2.5 h-2.5 rounded-full bg-rose-500" />
                        <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                        <span className="text-[10px] font-mono text-slate-500 ml-4">yaseero.com/admin/dashboard</span>
                    </div>
                    {/* Simulated Interface Grid */}
                    <div className="grid grid-cols-12 gap-3 p-4 bg-slate-950/40 text-left min-h-[320px] font-mono text-xs">
                        {/* Sidebar Mock */}
                        <div className="col-span-3 border-r border-slate-900 pr-3 space-y-4 hidden md:block">
                            <div className="h-6 bg-slate-900 rounded-lg w-3/4" />
                            <div className="space-y-2 pt-4">
                                <div className="h-8 bg-indigo-500/10 border border-indigo-500/20 rounded-lg w-full" />
                                <div className="h-8 bg-slate-900/40 rounded-lg w-full" />
                                <div className="h-8 bg-slate-900/40 rounded-lg w-full" />
                                <div className="h-8 bg-slate-900/40 rounded-lg w-full" />
                            </div>
                        </div>
                        {/* Body Mock */}
                        <div className="col-span-12 md:col-span-9 space-y-4">
                            <div className="flex justify-between items-center">
                                <div className="h-5 bg-slate-900 rounded-lg w-1/3" />
                                <div className="h-7 bg-emerald-500/10 border border-emerald-500/20 rounded-lg w-20" />
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div className="p-3 bg-slate-900/40 border border-slate-900 rounded-xl space-y-2">
                                    <span className="text-[10px] text-slate-500">TODAY ORDERS</span>
                                    <div className="text-lg font-black text-indigo-400">128 Orders</div>
                                </div>
                                <div className="p-3 bg-slate-900/40 border border-slate-900 rounded-xl space-y-2">
                                    <span className="text-[10px] text-slate-500">REVENUE</span>
                                    <div className="text-lg font-black text-emerald-400">AED 34,920</div>
                                </div>
                                <div className="p-3 bg-slate-900/40 border border-slate-900 rounded-xl space-y-2">
                                    <span className="text-[10px] text-slate-500">FULFILLMENT</span>
                                    <div className="text-lg font-black text-cyan-400">96.8%</div>
                                </div>
                            </div>
                            {/* Table Mock */}
                            <div className="border border-slate-900 rounded-xl p-3 bg-slate-900/20 space-y-2">
                                <div className="h-4 bg-slate-900 rounded w-full" />
                                <div className="h-px bg-slate-900 my-1" />
                                <div className="h-4 bg-slate-900 rounded w-5/6" />
                                <div className="h-4 bg-slate-900 rounded w-2/3" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Band */}
            <section className="bg-slate-950 border-y border-slate-900 py-12 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                    {stats.map((s, idx) => (
                        <div key={idx} className="space-y-1">
                            <p className="text-3xl sm:text-4xl font-black text-transparent bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text">
                                {s.val}
                            </p>
                            <p className="text-xs sm:text-sm font-bold text-slate-500 uppercase tracking-widest">{s.label}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 z-10 scroll-mt-10">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h2 className="text-xs font-black uppercase tracking-widest text-indigo-400">Built for Enterprise scale</h2>
                    <p className="mt-3 text-3xl sm:text-4xl font-black text-white">Digitalizing B2B Ordering Workflows</p>
                    <p className="mt-4 text-base sm:text-lg text-slate-400">
                        Yaseero provides advanced tools specifically tailored to solve wholesale and distribution log issues.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {features.map((f, idx) => (
                        <div 
                            key={idx} 
                            className="bg-slate-950/40 border border-slate-900 rounded-2xl p-6 hover:border-indigo-500/40 hover:bg-slate-900/20 transition-all duration-300 group"
                        >
                            <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center border border-slate-800 shadow-inner group-hover:scale-105 group-hover:border-indigo-500/20 transition-all">
                                {f.icon}
                            </div>
                            <h3 className="text-lg font-bold text-white mt-4">{f.title}</h3>
                            <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 z-10 scroll-mt-10">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h2 className="text-xs font-black uppercase tracking-widest text-indigo-400">Flexible Pricing</h2>
                    <p className="mt-3 text-3xl sm:text-4xl font-black text-white">SaaS Plans Built for Wholesalers</p>
                    <p className="mt-4 text-base text-slate-400">
                        Choose a billing style that matches your picking and delivery scale.
                    </p>

                    {/* Toggle */}
                    <div className="mt-8 inline-flex items-center gap-3 bg-slate-900 p-1 rounded-xl border border-slate-800 select-none">
                        <button 
                            onClick={toggleBilling} 
                            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${!isAnnual ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}
                        >
                            Monthly Billing
                        </button>
                        <button 
                            onClick={toggleBilling} 
                            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${isAnnual ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}
                        >
                            <span>Annual Billing</span>
                            <span className="bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter">Save 20%</span>
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
                    {pricingTiers.map((t, idx) => (
                        <div 
                            key={idx} 
                            className={`bg-slate-950/40 border rounded-3xl p-8 flex flex-col relative transition-all duration-300 ${
                                t.popular 
                                    ? 'border-indigo-500 shadow-lg shadow-indigo-500/5 bg-gradient-to-b from-indigo-950/20 to-slate-950/40 scale-[1.02] z-10' 
                                    : 'border-slate-900 hover:border-slate-800'
                            }`}
                        >
                            {t.popular && (
                                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-indigo-500 text-white text-[10px] font-black tracking-widest uppercase px-3 py-1 rounded-full shadow-md">
                                    Most Popular
                                </span>
                            )}
                            <div>
                                <h3 className="text-xl font-bold text-white">{t.name}</h3>
                                <p className="mt-2 text-xs text-slate-400 font-medium leading-relaxed min-h-[2.5rem]">{t.desc}</p>
                                <div className="mt-6 flex items-baseline gap-1">
                                    <span className="text-4xl font-black text-white">
                                        {typeof t.price === 'number' ? `AED ${t.price}` : t.price}
                                    </span>
                                    {typeof t.price === 'number' && (
                                        <span className="text-xs text-slate-500 font-bold">/ month</span>
                                    )}
                                </div>
                            </div>
                            
                            <ul className="mt-8 space-y-3.5 flex-1">
                                {t.features.map((feat, fidx) => (
                                    <li key={fidx} className="flex items-start gap-2.5 text-xs text-slate-300 font-medium">
                                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                                        <span>{feat}</span>
                                    </li>
                                ))}
                            </ul>

                            <a 
                                href={t.name === 'Enterprise' ? '#contact' : '/register'}
                                className={`mt-8 w-full py-3 rounded-xl text-xs font-black uppercase tracking-wider text-center shadow-sm transition-all duration-200 active:scale-95 ${
                                    t.popular 
                                        ? 'bg-gradient-to-r from-indigo-500 to-cyan-500 text-white hover:shadow-lg hover:shadow-indigo-500/20' 
                                        : 'border-2 border-slate-800 text-slate-300 hover:border-slate-700 hover:text-white'
                                }`}
                            >
                                {t.cta}
                            </a>
                        </div>
                    ))}
                </div>
            </section>

            {/* About Section */}
            <section id="about" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 z-10 border-t border-slate-900/50 scroll-mt-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <div className="space-y-6">
                        <h2 className="text-xs font-black uppercase tracking-widest text-indigo-400">Our Mission</h2>
                        <p className="text-3xl sm:text-4xl font-black text-white leading-tight">
                            Bridging Suppliers & Retailers through Digital Workflows
                        </p>
                        <p className="text-sm sm:text-base text-slate-400 leading-relaxed">
                            Yaseero was founded with a simple goal: to eliminate paper ordering logs and invoice discrepancies between local wholesale suppliers and retailers. By digitalizing the B2B supply chain, we offer suppliers complete visibility into order picks, dispatcher logs, and customer credits.
                        </p>
                        <div className="space-y-4 pt-2">
                            <div className="flex gap-3">
                                <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center border border-indigo-500/20 flex-shrink-0">
                                    <Lock className="w-5 h-5 text-indigo-400" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-white text-sm">Offline-Ready Resilience</h4>
                                    <p className="text-xs text-slate-400 mt-1">Our customer portals and picking lists are optimized to buffer updates offline when mobile connectivity is unstable.</p>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20 flex-shrink-0">
                                    <Activity className="w-5 h-5 text-cyan-400" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-white text-sm">Unified System Audit</h4>
                                    <p className="text-xs text-slate-400 mt-1">Every state transition (Creation, Revision suggestion, Re-route, Delivery) triggers a permanent, traceable workflow history record.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Order Workflow Timeline Mockup */}
                    <div className="bg-slate-950 border border-slate-900 rounded-3xl p-6 relative overflow-hidden flex flex-col justify-center min-h-[300px]">
                        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-transparent pointer-events-none" />
                        <h4 className="text-xs font-mono font-bold text-slate-400 uppercase tracking-widest mb-4">Simulated B2B Order Lifecycle</h4>
                        <div className="space-y-4 font-mono text-[11px] text-slate-500">
                            <div className="flex gap-3 items-start">
                                <div className="w-5 h-5 rounded-full bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 flex items-center justify-center font-bold shrink-0 mt-0.5">1</div>
                                <div className="flex-1">
                                    <div className="flex justify-between text-xs font-bold text-white">
                                        <span>Order Placed by Retailer</span>
                                        <span className="text-slate-600">10:00 AM</span>
                                    </div>
                                    <p className="text-[11px] text-slate-500 mt-0.5">Checked against supplier stock index for confirmation</p>
                                </div>
                            </div>
                            <div className="flex gap-3 items-start">
                                <div className="w-5 h-5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center justify-center font-bold shrink-0 mt-0.5">2</div>
                                <div className="flex-1">
                                    <div className="flex justify-between text-xs font-bold text-white">
                                        <span>Warehouse picking log check</span>
                                        <span className="text-slate-600">10:15 AM</span>
                                    </div>
                                    <p className="text-[11px] text-slate-500 mt-0.5">Picker logs quantity revisions if items are out of stock</p>
                                </div>
                            </div>
                            <div className="flex gap-3 items-start">
                                <div className="w-5 h-5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 flex items-center justify-center font-bold shrink-0 mt-0.5">3</div>
                                <div className="flex-1">
                                    <div className="flex justify-between text-xs font-bold text-white">
                                        <span>Dispatched for Delivery</span>
                                        <span className="text-slate-600">01:00 PM</span>
                                    </div>
                                    <p className="text-[11px] text-slate-500 mt-0.5">Assigned to driver with matching vehicle license details</p>
                                </div>
                            </div>
                            <div className="flex gap-3 items-start">
                                <div className="w-5 h-5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center justify-center font-bold shrink-0 mt-0.5">4</div>
                                <div className="flex-1">
                                    <div className="flex justify-between text-xs font-bold text-white">
                                        <span>Delivered & Closed</span>
                                        <span className="text-slate-600">03:30 PM</span>
                                    </div>
                                    <p className="text-[11px] text-slate-500 mt-0.5">Storekeeper signs off; daily operations report is compiled</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 z-10 scroll-mt-10">
                <div className="bg-gradient-to-r from-indigo-950/20 via-slate-950 to-slate-950/40 border border-slate-900 rounded-3xl p-8 sm:p-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        {/* Info */}
                        <div className="lg:col-span-5 space-y-6">
                            <h2 className="text-xs font-black uppercase tracking-widest text-indigo-400">Contact Us</h2>
                            <p className="text-3xl font-black text-white">Let's talk business</p>
                            <p className="text-sm text-slate-400 leading-relaxed">
                                Interested in digitalizing your wholesale distribution channels? Reach out to our systems integration team.
                            </p>
                            <div className="space-y-4 pt-2">
                                <div className="flex items-center gap-3 text-xs text-slate-300">
                                    <Mail className="w-5 h-5 text-indigo-400" />
                                    <span>integration@yaseero.com</span>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-slate-300">
                                    <Phone className="w-5 h-5 text-indigo-400" />
                                    <span>+971 (0) 4 567 8900</span>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-slate-300">
                                    <MapPin className="w-5 h-5 text-indigo-400" />
                                    <span>One Central, DWTC, Dubai, UAE</span>
                                </div>
                            </div>
                        </div>
                        {/* Interactive Form */}
                        <form onSubmit={handleContactSubmit} className="lg:col-span-7 space-y-4 text-xs">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">Your Name</label>
                                    <input 
                                        type="text" 
                                        required
                                        value={contactForm.name}
                                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                                        placeholder="Tom Wilson"
                                        className="w-full bg-slate-900/60 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 placeholder-slate-600 transition-colors"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">Your Email</label>
                                    <input 
                                        type="email" 
                                        required
                                        value={contactForm.email}
                                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                                        placeholder="tom@example.com"
                                        className="w-full bg-slate-900/60 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 placeholder-slate-600 transition-colors"
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">Message</label>
                                <textarea 
                                    required
                                    rows={4}
                                    value={contactForm.message}
                                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                                    placeholder="Tell us about your logistics and supplier scale..."
                                    className="w-full bg-slate-900/60 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 placeholder-slate-600 transition-colors resize-none"
                                />
                            </div>

                            {formSubmitted && (
                                <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl flex items-center gap-2 font-bold animate-fade-in">
                                    <CheckCircle2 size={16} />
                                    <span>Thank you! Your message was submitted successfully. Our team will reach out shortly.</span>
                                </div>
                            )}

                            <button 
                                type="submit"
                                disabled={submitting || formSubmitted}
                                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 px-6 rounded-xl flex items-center justify-center gap-2 transition-all shadow-md shadow-indigo-600/20 disabled:opacity-50"
                            >
                                {submitting ? (
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" />
                                        <span>Send Message</span>
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="w-full bg-slate-950 border-t border-slate-900 py-10 mt-auto z-10 text-xs">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-gradient-to-r from-indigo-500 to-cyan-500 flex items-center justify-center">
                            <Layers className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-black text-white tracking-tight text-base">yaseero</span>
                    </div>

                    <p className="text-slate-500 font-medium text-center md:text-left">
                        &copy; {new Date().getFullYear()} Yaseero Platform. All rights reserved.
                    </p>

                    <div className="flex gap-6 text-slate-400 font-semibold">
                        <a href="#features" className="hover:text-white transition-colors">Features</a>
                        <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
                        <a href="#about" className="hover:text-white transition-colors">About</a>
                        <a href="#contact" className="hover:text-white transition-colors">Contact</a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
